import * as React from 'react';
import { inject, observer } from 'mobx-react';
import * as styles from "./Panel.css";
import { ThemeStore } from './../stores';
import { IAPanel } from '../../panels/Panel';
import { IAColorPicker } from '../../colorPicker/ColorPicker';
import { IATextField } from '../../textField/TextField';
import { IAButton } from '../../button/Button';
import ThemeService from '../../../services/ThemeService';
import { ITheme as IATheme } from '../../../interfaces/ITheme';
import { BaseSlots, ThemeGenerator, themeRulesStandardCreator, IThemeRules, FabricSlots } from 'office-ui-fabric-react/lib/ThemeGenerator';
import { createTheme, IPalette } from 'office-ui-fabric-react/lib/Styling';
import { getColorFromString, IColor } from 'office-ui-fabric-react/lib/Color';
import { isDark } from 'office-ui-fabric-react/lib/utilities/color/shades';
import { IThemePalette } from '../../../interfaces/IThemePalette';
import { IAIcon } from '../../icon/Icon';
import { LocalizationService } from '../../../services/LocalizationService';
import { Helper } from '../../../Helper';

export interface IViewPanelProps {
  themeStore?: ThemeStore;
  themeSelected: IATheme;
}

export interface IViewPanelState {
  themeLighterAlt?: string;
}

@inject('themeStore')
@observer
export default class ViewPanel extends React.Component<IViewPanelProps, IViewPanelState> {

  private _colorChangeTimeout: number;
  private themeRules: IThemeRules;

  constructor(props) {
    super(props);
  }

  componentWillReceiveProps() {
    if (this.props.themeStore.themeSelected != undefined && !this.props.themeStore.isDeleteMode) {
      this._buildInitialState(true);
    }
  }

  private async save(): Promise<void> {
    this.props.themeStore.saving = true;
    await ThemeService.updateTheme(this.props.themeStore.environment, this.props.themeStore.token, this.props.themeStore.tenant, this.props.themeStore.themeSelected);
    this.props.themeStore.saving = false;
  }

  private getThemes(): void {
    this.props.themeStore.loading = true;
    // Get tenant themes
    ThemeService.getThemes(this.props.themeStore.environment, this.props.themeStore.token, this.props.themeStore.tenant).then((themes: IATheme[]) => {
      // Set themes
      this.props.themeStore.themes = themes;
      this.props.themeStore.loading = false;
    });
  }

  private _buildInitialState(isCustomization: boolean): void {
    // ThemeRules cannot be added to mobx store. Error: "Maximum call stack size exceeded". The dependent rules are recurrent.
    // If it needs to be added, the recurrent depedent rules have to be removed.
    const themeRules = themeRulesStandardCreator();
    const colors = {
      primaryColor: getColorFromString(this.props.themeStore.themeSelected.Palette.themePrimary)!,
      textColor: getColorFromString(this.props.themeStore.themeSelected.Palette.neutralPrimary)!,
      backgroundColor: getColorFromString(this.props.themeStore.themeSelected.Palette.white)!,
      secondaryColor: getColorFromString(this.props.themeStore.themeSelected.Palette.themeLighterAlt)!
    };
    ThemeGenerator.insureSlots(themeRules, isDark(themeRules[BaseSlots[BaseSlots.backgroundColor]].color!));
    ThemeGenerator.setSlot(themeRules[BaseSlots[BaseSlots.primaryColor]], colors.primaryColor, undefined, isCustomization, isCustomization);
    ThemeGenerator.setSlot(themeRules[BaseSlots[BaseSlots.foregroundColor]], colors.textColor, undefined, isCustomization, isCustomization);
    ThemeGenerator.setSlot(themeRules[FabricSlots[FabricSlots.white]], colors.backgroundColor, undefined, isCustomization, isCustomization);
    ThemeGenerator.setSlot(themeRules[FabricSlots[FabricSlots.themeLighterAlt]], colors.secondaryColor, undefined, isCustomization, isCustomization);

    const themeAsJson: {
      [key: string]: string;
    } = ThemeGenerator.getThemeAsJson(themeRules);

    const finalTheme = createTheme({
      ...{ palette: themeAsJson },
      isInverted: isDark(themeRules[BaseSlots[BaseSlots.backgroundColor]].color!)
    });

    // Set initial state
    this.props.themeStore.themeSelected.Palette = this.convertToThemePalette(finalTheme.palette);
    this.props.themeStore.themeSelected.IsInverted = finalTheme.isInverted;
    this.themeRules = themeRules;
    this.setState({ themeLighterAlt: this.props.themeStore.themeSelected.Palette.themeLighterAlt });
  }

  private _onColorChange(colorToChange: string, baseSlot: BaseSlots | FabricSlots, newColor: IColor | undefined): void {
    if (this._colorChangeTimeout) {
      window.clearTimeout(this._colorChangeTimeout);
    }
    if (newColor) {
      if (colorToChange === this.props.themeStore.themeSelected.Palette.themePrimary) {
        this.props.themeStore.themeSelected.Palette.themePrimary = `#${newColor.hex}`;
      } else if (colorToChange === this.props.themeStore.themeSelected.Palette.neutralPrimary) {
        this.props.themeStore.themeSelected.Palette.neutralPrimary = `#${newColor.hex}`;
      } else if (colorToChange === this.props.themeStore.themeSelected.Palette.white) {
        this.props.themeStore.themeSelected.Palette.white = `#${newColor.hex}`;
      } else if (colorToChange === this.props.themeStore.themeSelected.Palette.themeLighterAlt) {
        this.props.themeStore.themeSelected.Palette.themeLighterAlt = `#${newColor.hex}`;
      } else {
        return;
      }
      const themePrimary = this.props.themeStore.themeSelected.Palette.themePrimary;
      const neutralPrimary = this.props.themeStore.themeSelected.Palette.neutralPrimary;
      const white = this.props.themeStore.themeSelected.Palette.white;
      const themeLighterAlt = this.props.themeStore.themeSelected.Palette.themeLighterAlt;
      this._colorChangeTimeout = window.setTimeout(() => {
        const themeRules = this.themeRules;
        if (themeRules) {
          const currentIsDark = isDark(themeRules[BaseSlots[BaseSlots.backgroundColor]].color!);
          ThemeGenerator.setSlot(themeRules[BaseSlots[baseSlot]], newColor, currentIsDark, true, true);
          if (currentIsDark !== isDark(themeRules[BaseSlots[BaseSlots.backgroundColor]].color!)) {
            // isInverted got swapped, so need to refresh slots with new shading rules
            ThemeGenerator.insureSlots(themeRules, currentIsDark);
          }
        }
        this.themeRules = themeRules;
        this._makeNewTheme();
        // Make sure the selected values are not changed
        this.props.themeStore.themeSelected.Palette.themePrimary = themePrimary;
        this.props.themeStore.themeSelected.Palette.neutralPrimary = neutralPrimary;
        this.props.themeStore.themeSelected.Palette.white = white;
        this.props.themeStore.themeSelected.Palette.themeLighterAlt = themeLighterAlt;
      }, 20);
      // 20ms is low enough that you can slowly drag to change color and see that theme,
      // but high enough that quick changes don't get bogged down by a million changes inbetween
    }
  }

  private _makeNewTheme(): void {
    if (this.themeRules) {
      const themeAsJson: {
        [key: string]: string;
      } = ThemeGenerator.getThemeAsJson(this.themeRules);

      const finalTheme = createTheme({
        ...{ palette: themeAsJson },
        isInverted: isDark(this.themeRules[BaseSlots[BaseSlots.backgroundColor]].color!)
      });
      this.props.themeStore.themeSelected.Palette = this.convertToThemePalette(finalTheme.palette);
      this.props.themeStore.themeSelected.IsInverted = finalTheme.isInverted;
      this.props.themeStore.themeSelected.Palette.themeLighterAlt = this.state.themeLighterAlt;
    }
  }

  public render(): JSX.Element {
    const showPanel: boolean = this.props.themeStore.themeSelected != undefined && !this.props.themeStore.isDeleteMode;
    let content: JSX.Element;
    if (showPanel) {
      content = (
        <div className={styles.viewPanelContentWrapper}>
          <div>
            {this.props.themeStore.isAddMode &&
              <div>
                <div className={styles.section}>
                  <IATextField
                    label={new LocalizationService().strings.Theme_Field_Name}
                    placeholder={new LocalizationService().strings.Theme_Field_Name_Placeholder}
                    text={this.props.themeStore.themeSelected ? this.props.themeStore.themeSelected.Name : ""}
                    onChange={(themeName: string) => {
                      this.props.themeStore.themeSelected.Name = themeName;
                    }}
                  />
                </div>
                <div className={styles.infoText} style={{ float: "none", margin: 0 }}>
                  <IAIcon
                    title={"Info"}
                    size={16}
                    color={"gray"}
                    style={{
                      float: "left",
                      height: 16,
                      width: 16,
                      paddingRight: 5,
                    }}
                  />
                  <span>
                    {new LocalizationService().strings.Theme_Field_Name_Disclaimer}
                  </span>
                </div>
              </div>
            }
            <div className={styles.section}>
              <IAColorPicker
                label={new LocalizationService().strings.Theme_Field_Primary_Color}
                color={this.props.themeStore.themeSelected ? this.props.themeStore.themeSelected.Palette.themePrimary : ""}
                showClearButton={false}
                onChange={(primaryColor: string) => {
                  this._onColorChange(this.props.themeStore.themeSelected.Palette.themePrimary, BaseSlots.primaryColor, getColorFromString(primaryColor));
                }}
                styles={{
                  clear: "both",
                  width: "100%"
                }}
              />
              <IAColorPicker
                label={new LocalizationService().strings.Theme_Field_Text_Color}
                color={this.props.themeStore.themeSelected ? this.props.themeStore.themeSelected.Palette.neutralPrimary : ""}
                showClearButton={false}
                onChange={(foregroundColor: string) => {
                  this._onColorChange(this.props.themeStore.themeSelected.Palette.neutralPrimary, BaseSlots.foregroundColor, getColorFromString(foregroundColor));
                }}
                styles={{
                  clear: "both",
                  width: "100%"
                }}
              />
              <IAColorPicker
                label={new LocalizationService().strings.Theme_Field_Background_Color}
                color={this.props.themeStore.themeSelected ? this.props.themeStore.themeSelected.Palette.white : ""}
                showClearButton={false}
                onChange={(backgroundColor: string) => {
                  this._onColorChange(this.props.themeStore.themeSelected.Palette.white, FabricSlots.white, getColorFromString(backgroundColor));
                }}
                styles={{
                  clear: "both",
                  width: "100%"
                }}
              />
              <IAColorPicker
                label={new LocalizationService().strings.Theme_Field_Secondary_Color}
                color={this.props.themeStore.themeSelected ? this.props.themeStore.themeSelected.Palette.themeLighterAlt : ""}
                showClearButton={false}
                onChange={(secondaryColor: string) => {
                  if (Helper.isLightColor(secondaryColor) !== Helper.isLightColor(this.props.themeStore.themeSelected.Palette.neutralPrimary)) {
                    this.props.themeStore.themeSelected.Palette.themeLighterAlt = secondaryColor;
                    this.setState({
                      themeLighterAlt: secondaryColor
                    });
                  } else {
                    if (Helper.isLightColor(this.props.themeStore.themeSelected.Palette.neutralPrimary)) {
                      console.log(this.state.themeLighterAlt)
                      this.setState({
                        themeLighterAlt: "#333333"
                      });
                      this.props.themeStore.themeSelected.Palette.themeLighterAlt = "#333333";
                      alert(new LocalizationService().strings.Theme_darkerColor);
                    } else {
                      console.log(this.state.themeLighterAlt)
                      this.setState({
                        themeLighterAlt: "#ffffff"
                      });
                      this.props.themeStore.themeSelected.Palette.themeLighterAlt = "#ffffff";
                      alert(new LocalizationService().strings.Theme_lighterColor);
                    }
                  }
                }}
                styles={{
                  clear: "both",
                  width: "100%"
                }}
              />
            </div>
            {this.props.themeStore.showEditInfoText &&
              <div className={styles.infoText}>
                <IAIcon
                  title={"Info"}
                  size={16}
                  color={"gray"}
                  style={{
                    float: "left",
                    height: 16,
                    width: 16,
                    paddingRight: 5
                  }}
                />
                <div>
                  {new LocalizationService().strings.Theme_Edit_Info_Text}
                </div>
              </div>
            }
            <IAButton
              label={new LocalizationService().strings.Messages_EditPanel_SaveAndClose}
              disbaled={
                !this.props.themeStore.themeSelected.Name ||
                !this.props.themeStore.themeSelected.Palette.themePrimary ||
                !this.props.themeStore.themeSelected.Palette.neutralPrimary ||
                !this.props.themeStore.themeSelected.Palette.white ||
                !this.props.themeStore.themeSelected.Palette.themeLighterAlt ||
                !this.props.themeStore.isThemeChanged()
              }
              buttonColor="#02a5e2"
              onClick={() => {
                this.save().then(() => {
                  localStorage.removeItem("IntraActive_Theme_Selected");
                  this.props.themeStore.themeSelected = undefined
                  this.getThemes();
                });
              }}
              showSpinner={this.props.themeStore.saving}
              dataAutomationId="theme-panel-save"
              style={{
                marginTop: 20,
                clear: "both",
                float: "left",
                position: "absolute",
                bottom: "30px"
              }}
            />
          </div>
        </div>
      );
    }
    return (
      <div>
        <IAPanel
          showNavigationBar={true}
          open={showPanel}
          transition={this.props.themeStore.themeSelected != undefined ? "fade" : "slide"}
          width={550}
          isMobile={false}
          close={() => {
            if (this.props.themeStore.isAddMode == false) {
              this.props.themeStore.resetTheme();
            }
            this.props.themeStore.themeSelected = undefined;
          }}
        >{content}</IAPanel>
      </div>
    );
  }

  private convertToThemePalette(fullPalette: IPalette): IThemePalette {
    let palette: IThemePalette = {
      themePrimary: fullPalette.themePrimary,
      themeLighterAlt: fullPalette.themeLighterAlt,
      themeLighter: fullPalette.themeLighter,
      themeLight: fullPalette.themeLight,
      themeTertiary: fullPalette.themeTertiary,
      themeSecondary: fullPalette.themeSecondary,
      themeDarkAlt: fullPalette.themeDarkAlt,
      themeDark: fullPalette.themeDark,
      themeDarker: fullPalette.themeDarker,
      neutralLighterAlt: fullPalette.neutralLighterAlt,
      neutralLighter: fullPalette.neutralLighter,
      neutralLight: fullPalette.neutralLight,
      neutralQuaternaryAlt: fullPalette.neutralQuaternaryAlt,
      neutralQuaternary: fullPalette.neutralQuaternary,
      neutralTertiaryAlt: fullPalette.neutralTertiaryAlt,
      neutralTertiary: fullPalette.neutralTertiary,
      neutralSecondary: fullPalette.neutralSecondary,
      neutralPrimaryAlt: fullPalette.neutralPrimaryAlt,
      neutralPrimary: fullPalette.neutralPrimary,
      neutralDark: fullPalette.neutralDark,
      black: fullPalette.black,
      white: fullPalette.white,
    };
    return palette;
  }
}